import { convertObjToQueryStr } from "@utils/utils"
import { api } from "./api"
import _ from "lodash"
import { useLangRefetch } from "./utils"
import type { components, WithId, JsonResponse, JsonBody, paths } from "./generated"

export type RootGroup = WithId<components["schemas"]["RootGroup"]>
type Group = WithId<components["schemas"]["Group"]>
type RootGroupPopulated = RootGroup & { groups?: Group[] }
type PaginationObj = components["schemas"]["PaginationObj"]
type Paginated<T> = Omit<PaginationObj, "docs"> & { docs?: T[] }
type ObjectId = components["schemas"]["ObjectId"]

type ClubSupportPlayersResponse = JsonResponse<paths["/api/root_group/{id}/club-support/players"]["get"]>
type PassiveResponse = paths["/api/root_group/{id}/setPassive"]["post"]["responses"][200]["content"]["text/plain"]

const rootGroupApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMyRootGroups: builder.query<RootGroupPopulated[], void>({
			query: () => "/root_group/own",
			providesTags: (result) => {
				if (!result) return []
				return result
					.map((doc) => ({ type: "RootGroup" as const, id: doc._id }))
					.concat([{ type: "RootGroup" as const, id: "LIST" }])
			},
		}),
		searchRootGroups: builder.query<Paginated<RootGroupPopulated>, Record<string, unknown>>({
			query: (queryObj) => {
				if (queryObj.query) {
					queryObj = queryObj.query as Record<string, unknown>
				}
				return `/root_group/search${convertObjToQueryStr(queryObj)}`
			},
			providesTags: () => {
				return ["RootGroup", "Group"]
			},
		}),
		getRootGroup: builder.query<
			RootGroup & { memberPool?: PaginationObj },
			{ id: string; paginationOpts?: Record<string, unknown> }
		>({
			query: ({ id, paginationOpts }) => `/root_group/${id}${convertObjToQueryStr(paginationOpts)}`,
			providesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }, "RootGroup", "Loc"]
			},
		}),
		getRootGroups: builder.query<RootGroupPopulated[], Record<string, unknown> | void>({
			query: (queryObj) => `/root_group${convertObjToQueryStr(queryObj)}`,
			providesTags: (result) => {
				if (!result) return []
				return result
					.map((doc) => ({ type: "RootGroup" as const, id: doc._id }))
					.concat([{ type: "RootGroup" as const, id: "LIST" }])
			},
		}),
		createRootGroup: builder.mutation<RootGroup, JsonBody<paths["/api/root_group"]["post"]>>({
			query: (body) => ({
				url: "/root_group",
				method: "POST",
				body: body,
			}),
			invalidatesTags: () => {
				return [{ type: "RootGroup", id: "LIST" }]
			},
		}),
		updateRootGroup: builder.mutation<RootGroup, { id: string; body: FormData | Partial<components["schemas"]["RootGroup"]> }>({
			query: ({ id, body }) => ({
				url: `/root_group/${id}`,
				method: "PUT",
				body: body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }]
			},
		}),
		getRootGroupClubSupportPlayers: builder.query<ClubSupportPlayersResponse, { id: string; targetYear?: number }>({
			query: ({ id, ...rest }) => ({
				url: `/root_group/${id}/club-support/players${convertObjToQueryStr(rest)}`,
				method: "GET",
			}),
			providesTags: (_result, _error, args) => [{ type: "ClubSupportPlayers", id: args.id }],
		}),
		getAllClubSupportPlayers: builder.query<components["schemas"]["ClubSupport"][], { targetYear?: number } | void>({
			query: (rest) => ({
				url: `/root_group/club-support/players${convertObjToQueryStr(rest)}`,
				method: "GET",
			}),
			providesTags: (result) => {
				if (!result) return []
				return result.map((o) => ({ type: "ClubSupportPlayers" as const, id: o.rootGroup }))
			},
		}),
		updateRootGroupClubSupportPlayers: builder.mutation<
			void,
			{ id: string; body: Record<string, string[]>; query?: { targetYear?: number; lock?: boolean } }
		>({
			query: ({ id, body, query }) => ({
				url: `/root_group/${id}/club-support/players`,
				method: "PUT",
				body: body,
				params: query,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "ClubSupportPlayers", id: args.id }]
			},
		}),
		unlockRootGroupClubSupportPlayersByField: builder.mutation<
			void,
			{ id: string; body: { field?: string }; query?: Record<string, unknown> }
		>({
			query: ({ id, body, query }) => ({
				url: `/root_group/${id}/club-support/players/unlock`,
				method: "POST",
				body: body,
				params: query,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "ClubSupportPlayers", id: args.id }]
			},
		}),
		updateRootGroupClubSupportPlayerMeta: builder.mutation<
			components["schemas"]["ClubSupport"],
			{
				rootGroupId: string
				profileId: string
				body: components["schemas"]["ClubSupportPlayerMeta"]
				query?: { targetYear?: number; field?: "academyPlayers" | "talentPlayers"; recalculateRules?: string[] }
			}
		>({
			query: ({ rootGroupId, profileId, body, query }) => ({
				url: `/root_group/${rootGroupId}/club-support/players/updateMeta/${profileId}${convertObjToQueryStr(query)}`,
				method: "PUT",
				body: body,
			}),
			invalidatesTags: (_result, _error, args) => {
				const arr: { type: "ClubSupportPlayers" | "RootGroup"; id: string }[] = [
					{ type: "ClubSupportPlayers", id: args.rootGroupId },
				]
				if (args.query?.recalculateRules) {
					arr.push({ type: "RootGroup", id: args.rootGroupId })
				}
				return arr
			},
		}),
		deleteRootGroup: builder.mutation<void, string>({
			query: (id) => ({
				url: `/root_group/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args }]
			},
		}),
		getSubGroups: builder.query<WithId<components["schemas"]["Group"]>[], string>({
			query: (id) => `/root_group/${id}/groups`,
			providesTags: (result, _error, arg) => {
				if (!result) return []
				const tags: { type: "Group" | "RootGroup"; id: string }[] = result.map((doc) => ({
					type: "Group",
					id: doc._id,
				}))
				tags.push({ type: "Group", id: "LIST" }, { type: "RootGroup", id: arg })
				return tags
			},
		}),
		addRootGroupPatron: builder.mutation<RootGroup, { id: string; body: { profileId?: ObjectId; customTitle?: string } }>({
			query: ({ id, body }) => ({
				url: `/root_group/${id}/patron`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [{ type: "RootGroup", id: arg.id }]
			},
		}),
		deleteRootGroupPatron: builder.mutation<RootGroup, { id: string; body: { profileId?: ObjectId } }>({
			query: ({ id, body }) => ({
				url: `/root_group/${id}/patron`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [{ type: "RootGroup", id: arg.id }]
			},
		}),
		addRootGroupMember: builder.mutation<RootGroup, { id: string; body: { profileIds?: ObjectId[] } }>({
			query: ({ id, body }) => ({
				url: `/root_group/${id}/member`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [{ type: "RootGroup", id: arg.id }]
			},
		}),
		deleteRootGroupMember: builder.mutation<RootGroup, { id: string; profileId: string; body?: Record<string, unknown> }>({
			query: ({ id, profileId, body }) => ({
				url: `/root_group/${id}/member/${profileId}`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [
					{ type: "RootGroup", id: arg.id },
					{ type: "Profile", id: arg.profileId },
				]
			},
		}),
		deleteRootGroupMembers: builder.mutation<RootGroup, { id: string; body: { profileIds?: ObjectId[] } }>({
			query: ({ id, body }) => ({
				url: `/root_group/${id}/member`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				const tags: { type: "RootGroup" | "Profile"; id: string }[] = [{ type: "RootGroup", id: arg.id }]
				return tags.concat(arg?.body?.profileIds?.map((pId) => ({ type: "Profile" as const, id: pId })) ?? [])
			},
		}),
		cancelRootGroupInvite: builder.mutation<RootGroup, { rootId: string; inviteId: string; groupId?: string; profile?: string }>({
			query: ({ rootId, inviteId }) => ({
				url: `/root_group/${rootId}/cancelInvite/${inviteId}`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, arg) => {
				return _.compact([
					{ type: "RootGroup" as const, id: arg.rootId },
					arg.groupId && { type: "Group" as const, id: arg.groupId },
					arg.profile && { type: "Profile" as const, id: arg.profile },
				])
			},
		}),
		setPassiveRootGroup: builder.mutation<PassiveResponse, { rootGroupId: string; groups?: boolean }>({
			query: ({ rootGroupId, groups }) => ({
				url: `/root_group/${rootGroupId}/setPassive`,
				method: "POST",
				body: { groups },
			}),
			invalidatesTags: () => {
				return ["RootGroup", "Group", "Profile"]
			},
		}),
		unsetPassiveRootGroup: builder.mutation<PassiveResponse, { rootGroupId: string; groups?: boolean }>({
			query: ({ rootGroupId, groups }) => ({
				url: `/root_group/${rootGroupId}/unsetPassive`,
				method: "POST",
				body: { groups },
			}),
			invalidatesTags: () => {
				return ["RootGroup", "Group", "Profile"]
			},
		}),
		resendEmail: builder.mutation<string, { rootGroupId: string; body: { confirmationId?: string }; groupId?: string }>({
			query: ({ rootGroupId, body }) => ({
				url: `/root_group/${rootGroupId}/resendemail`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				const arr: { type: "RootGroup" | "Group"; id: string }[] = [{ type: "RootGroup", id: arg.rootGroupId }]
				if (arg.groupId) {
					arr.push({ type: "Group", id: arg.groupId })
				}
				return arr
			},
		}),
		addRoleMember: builder.mutation<RootGroup, { rootId: string; body: { name?: string; profileIds?: ObjectId[] } }>({
			query: ({ rootId, body }) => ({
				url: `/root_group/${rootId}/roleMember`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [{ type: "RootGroup", id: arg.rootId }]
			},
		}),
		removeRoleMember: builder.mutation<RootGroup, { rootId: string; profileId: string; body: { role?: string } }>({
			query: ({ rootId, profileId, body }) => ({
				url: `/root_group/${rootId}/roleMember/${profileId}`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				return [
					{ type: "RootGroup", id: arg.rootId },
					{ type: "Profile", id: arg.profileId },
				]
			},
		}),
		getTasoClubList: builder.query<JsonResponse<paths["/api/root_group/{id}/taso/clubs"]["get"]>, { id: string }>({
			query: ({ id }) => ({
				url: `/root_group/${id}/taso/clubs`,
				method: "GET",
			}),
		}),
		getClubSupportRootGroupSettings: builder.query<
			JsonResponse<paths["/api/root_group/{id}/club-support/settings"]["get"]>,
			{ id: string }
		>({
			query: ({ id }) => ({
				url: `/root_group/${id}/club-support/settings`,
			}),
			providesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }]
			},
		}),
		updateClubSupportRootGroupSettings: builder.mutation<
			components["schemas"]["ClubSupport"],
			{ id: string; body: Record<string, any> }
		>({
			query: ({ id, body }) => ({
				url: `/root_group/${id}/club-support/settings`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }]
			},
		}),
	}),
})

export const {
	useGetMyRootGroupsQuery,
	useGetRootGroupsQuery,
	useCreateRootGroupMutation,
	useUpdateRootGroupMutation,
	useGetRootGroupClubSupportPlayersQuery,
	useGetAllClubSupportPlayersQuery,
	useUpdateRootGroupClubSupportPlayersMutation,
	useUnlockRootGroupClubSupportPlayersByFieldMutation,
	useUpdateRootGroupClubSupportPlayerMetaMutation,
	useGetRootGroupQuery,
	useGetSubGroupsQuery,
	useAddRootGroupPatronMutation,
	useDeleteRootGroupPatronMutation,
	useAddRootGroupMemberMutation,
	useDeleteRootGroupMemberMutation,
	useDeleteRootGroupMembersMutation,
	useCancelRootGroupInviteMutation,
	useSearchRootGroupsQuery,
	useSetPassiveRootGroupMutation,
	useUnsetPassiveRootGroupMutation,
	useResendEmailMutation,
	useAddRoleMemberMutation,
	useRemoveRoleMemberMutation,
	useGetTasoClubListQuery,
	useGetClubSupportRootGroupSettingsQuery,
	useUpdateClubSupportRootGroupSettingsMutation,
} = rootGroupApi

export const useGetRootGroupQueryL = (arg?: null | object, options?: Record<string, unknown>) =>
	useLangRefetch(useGetRootGroupQuery, arg, options)
